import {
  clampEraseRadius,
  eraseWheelPixels,
  resizeEraseRadius,
  type PrefStore,
} from "./erase-brush.ts";

/** Box, Circle, Lasso, then Off, then Box again. Off is the click-and-drag default. */
export const SELECT_TOOLS = ["box", "circle", "lasso", "off"] as const;
export type SelectTool = (typeof SELECT_TOOLS)[number];

/** Same pixel range as the erase brush, stored separately. */
export const SELECT_CIRCLE_MIN = 8;
export const SELECT_CIRCLE_MAX = 160;
export const SELECT_CIRCLE_DEFAULT = 44;

const TOOL_KEY = "mapus.select-tool";
const RADIUS_KEY = "mapus.select-circle-radius";

/** Box → Circle → Lasso → Off → Box. */
export function nextSelectTool(tool: SelectTool): SelectTool {
  if (tool === "box") return "circle";
  if (tool === "circle") return "lasso";
  if (tool === "lasso") return "off";
  return "box";
}

/** Box, Circle, and Lasso ignore point and line clicks. Off keeps them. */
export function selectToolIsolates(tool: SelectTool): boolean {
  return tool !== "off";
}

/**
 * An unmoved press on empty map clears the selection.
 * Box, Circle, and Lasso do that on a plain click, the same as Off.
 * Shift and Ctrl/Cmd stay add and subtract, so those clicks do not clear.
 */
export function emptyClickClears(
  tool: SelectTool,
  press: {
    selecting: boolean;
    toggling: boolean;
    selectButton: boolean;
    shift: boolean;
    ctrl: boolean;
    meta: boolean;
    /** A press on a point or line never clears while Box, Circle, or Lasso is on. */
    onFeature?: boolean;
  },
): boolean {
  if (press.toggling) return false;
  if (!press.selecting && !press.selectButton) return false;
  if (selectToolIsolates(tool) && press.onFeature) return false;
  if (selectToolIsolates(tool) && (press.shift || press.ctrl || press.meta)) return false;
  return true;
}

/**
 * Box, Circle, and Lasso open their drag even when the press starts on a point
 * or line. A click there still does nothing, because that press does not clear
 * and only a move past the threshold applies the tool.
 */
export function beginSelectGesture(press: {
  tool: SelectTool;
  onFeature: boolean;
  mode: "replace" | "add" | "subtract" | null;
  clears: boolean;
  selecting: boolean;
  toggling: boolean;
}): boolean {
  if (selectToolIsolates(press.tool) && press.onFeature) return press.mode != null;
  return press.mode != null || press.clears || press.selecting || press.toggling;
}

/**
 * A drag past the threshold runs the select tool. A click does not.
 * Clear only when the press was a plain select click that never moved.
 */
export function selectPressRelease(
  moved: boolean,
  clears: boolean,
  mode: "replace" | "add" | "subtract" | null,
): "clear" | "apply" | "idle" {
  if (!moved) return clears ? "clear" : "idle";
  return mode ? "apply" : "idle";
}

export function isSelectTool(value: unknown): value is SelectTool {
  return value === "box" || value === "circle" || value === "lasso" || value === "off";
}

/** Missing or unknown values stay Off, so a click still selects and a drag still moves a point. */
export function readSelectTool(store: PrefStore): SelectTool {
  try {
    const raw = store.getItem(TOOL_KEY);
    return isSelectTool(raw) ? raw : "off";
  } catch {
    return "off";
  }
}

export function writeSelectTool(store: PrefStore, tool: SelectTool): void {
  try {
    store.setItem(TOOL_KEY, tool);
  } catch {
    /* private mode */
  }
}

export function clampSelectCircleRadius(radius: number): number {
  return clampEraseRadius(radius);
}

/** Wheel up grows the circle. One typical notch of 100 px changes the radius by 8 px. */
export function resizeSelectCircleRadius(radius: number, deltaY: number): number {
  return resizeEraseRadius(radius, deltaY);
}

export function selectCircleWheelPixels(deltaY: number, deltaMode: number): number {
  return eraseWheelPixels(deltaY, deltaMode);
}

export function readSelectCircleRadius(store: PrefStore): number {
  try {
    const raw = store.getItem(RADIUS_KEY);
    if (raw == null || raw === "") return SELECT_CIRCLE_DEFAULT;
    return clampSelectCircleRadius(Number(raw));
  } catch {
    return SELECT_CIRCLE_DEFAULT;
  }
}

export function writeSelectCircleRadius(store: PrefStore, radius: number): void {
  try {
    store.setItem(RADIUS_KEY, String(clampSelectCircleRadius(radius)));
  } catch {
    /* private mode */
  }
}
